/**
 * TodaysSubmissionsWidget
 * 
 * Read-only operational snapshot for the Pending Approval page.
 * Shows today's timesheet submission counts grouped by source so
 * coordinators can triage their morning queue at a glance.
 * 
 *   12  Auto-submitted   (green)
 *    2  Admin closed     (amber)
 *    0  Manual submit    (grey)
 * 
 * Pure UI — does NOT change approval / billing / funding / claim logic.
 * RBAC: piggy-backs off the existing `timesheets.approve` permission of
 * the parent route — this widget is only rendered inside the approval
 * page and the underlying backend endpoint is permission-gated as well.
 */

import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, ShieldAlert, Hand, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const SOURCE_VISUAL = {
  auto_submitted_clockout: {
    Icon: Zap,
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  admin_closed: {
    Icon: ShieldAlert,
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  manual_submit: {
    Icon: Hand,
    bar: 'bg-gray-400',
    dot: 'bg-gray-400',
    text: 'text-gray-700',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
};

export default function TodaysSubmissionsWidget({ refreshKey = 0, className = '' }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.get('/timesheets/v2/stats/submissions-today');
      setData(res.data?.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  return (
    <Card className={className} data-testid="todays-submissions-widget">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-sm font-semibold">Today's Submissions</CardTitle>
          {data?.date && (
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="todays-submissions-date">
              {data.date} • {data.total} total
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="todays-submissions-refresh"
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : error ? (
          <p className="text-xs text-destructive" data-testid="todays-submissions-error">{error}</p>
        ) : data ? (
          (data.sources || []).map((src) => {
            const visual = SOURCE_VISUAL[src.key] || SOURCE_VISUAL.manual_submit;
            const { Icon } = visual;
            return (
              <div
                key={src.key}
                className={`flex items-center gap-3 rounded-lg border ${visual.border} ${visual.bg} px-3 py-2`}
                data-testid={`todays-submissions-${src.key}`}
              >
                <div className={`h-8 w-8 rounded-full ${visual.bar} flex items-center justify-center text-white shrink-0`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-2xl font-mono font-bold tabular-nums ${visual.text}`} data-testid={`todays-submissions-${src.key}-count`}>
                      {src.count}
                    </span>
                    {data.total > 0 && (
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {src.percentage}%
                      </span>
                    )}
                  </div>
                  <p className={`text-xs font-medium ${visual.text} truncate`}>{src.label}</p>
                </div>
              </div>
            );
          })
        ) : null}
      </CardContent>
    </Card>
  );
}
